/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { forwardRef } from "react";

import cn from "classnames";

import normalize from "utils/normalize";

import { LoadingDots } from "../Loaders";
import Stack from "../Stack";

import s from "./Input.module.css";

interface ISelect extends React.InputHTMLAttributes<HTMLSelectElement> {
  options: {
    id: number | string;
    value: string | number;
    label?: string;
    childClassName?: string;
    disabled?: boolean;
  }[];
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isRequired?: true;
  isLoading?: true;
  isValueById?: boolean;
  isMissing?: boolean;
  deselect?: boolean;
  defaultValueClassName?: string;
}

const checkIfNotSelected = (input: any) => {
  const value = normalize(input);
  return !value;
};

const Select = forwardRef<HTMLSelectElement, ISelect>(
  (
    {
      options,
      onChange,
      className,
      value,
      disabled,
      isLoading,
      isRequired,
      isValueById = false,
      isMissing = false,
      defaultValue,
      defaultValueClassName,
      deselect = false,
      ...rest
    }: ISelect,
    ref,
  ) => {
    const handleOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      e.currentTarget.blur();

      if (onChange) {
        onChange(e);
      }
    };

    let localValue = value;
    const isNotSelected = checkIfNotSelected(value);
    if (isNotSelected) {
      localValue = "NOT_SELECTED";
    }

    return (
      <Stack className="relative" test-id="U9DBTLGW">
        <select
          ref={ref}
          className={cn(s.root, s.selectInput, className, {
            "border-alert": isMissing,
            "!text-light-gray": isNotSelected || !value,
          })}
          onChange={handleOnChange}
          disabled={disabled}
          value={localValue}
          {...rest}
          test-id="zUCPTDJC"
        >
          {!isRequired && (
            <option
              id={`NOT_SELECTED`}
              disabled={deselect ? false : true}
              //@ts-ignore
              value={deselect ? null : "NOT_SELECTED"}
              className={cn(`text-light-gray`, defaultValueClassName)}
              test-id="de53FFu6"
            >
              {defaultValue || rest.placeholder || "Select an option..."}
            </option>
          )}

          {Array.isArray(options) &&
            options.length > 0 &&
            options.map(({ id, value, label, childClassName, disabled }, i) => {
              return (
                <option
                  key={`${id || i}.${label || i}`}
                  id={`${id}`}
                  value={isValueById ? id : value}
                  className={childClassName}
                  disabled={disabled}
                  test-id="b8UiEbdL"
                >
                  {label || value}
                </option>
              );
            })}
        </select>

        <div
          className={cn(
            isLoading ? "flex" : "hidden",
            "absolute right-12 top-[18px]",
          )}
          test-id="yc8PY2xe"
        >
          <LoadingDots test-id="4hsS5QoB" />
        </div>
      </Stack>
    );
  },
);

Select.displayName = "Select";
export default Select;
